import React, { useState, useEffect, useRef } from "react";
import {
  ArrowsRightLeftIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  QrCodeIcon,
  CubeIcon,
  CheckCircleIcon,
  ClockIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  TrashIcon,
  PlusIcon,
  SparklesIcon,
  PaperAirplaneIcon,
  BuildingOfficeIcon,
} from "@heroicons/react/24/outline";
import { request } from "config/request";
import { ListCommanWay } from "API/commonApi";
import { useQuery } from "@tanstack/react-query";
import UseToast from "utils/AfToast";
import Swal from "sweetalert2";
import Select from "react-select";
import { soundFX } from "../soundEffects";
import { isBoxQrFormat, isAwbFormat } from "./AwbPackingTab";
import { useAuthStore } from "store/auth";
import { STOCK_ITEM_OPTIONS, CATEGORY_OPTIONS } from "utils/constant";

const compactSelectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    background: "white",
    borderRadius: "10px",
    borderColor: state.isFocused ? "#0077B6" : "#cbd5e1",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(0, 119, 182, 0.15)" : "none",
    minHeight: "38px",
    fontSize: "12px",
  }),
  valueContainer: (base: any) => ({ ...base, padding: "0 10px" }),
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
};

export const TransfersTab: React.FC = () => {
  const user = useAuthStore((s) => s.user);
  const userSubbranchId = (user as any)?.subbranch;

  const [activeSubtab, setActiveSubtab] = useState<"incoming" | "outgoing">("incoming");
  const [transfers, setTransfers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  // Filters
  const [selectedBranchFilter, setSelectedBranchFilter] = useState<any>(null);
  const [statusFilter, setStatusFilter] = useState<string>("ALL");

  // Fast handheld in-scan state
  const [fastScanInput, setFastScanInput] = useState("");
  const [fastScanLoading, setFastScanLoading] = useState(false);
  const [fastScanResult, setFastScanResult] = useState<any>(null);
  const fastScanInputRef = useRef<HTMLInputElement>(null);

  // Dispatch Modal state
  const [dispatchModalOpen, setDispatchModalOpen] = useState(false);
  const [dispatchMode, setDispatchMode] = useState<"bulk" | "qr">("bulk");
  const [dispatchName, setDispatchName] = useState<any>(STOCK_ITEM_OPTIONS[2] || STOCK_ITEM_OPTIONS[0]); // Default "5 Ltr"
  const [dispatchCategory, setDispatchCategory] = useState<any>(CATEGORY_OPTIONS[0]); // Packaging
  const [dispatchFromBranch, setDispatchFromBranch] = useState<any>(null);
  const [dispatchToBranch, setDispatchToBranch] = useState<any>(null);
  const [dispatchQty, setDispatchQty] = useState<string>("5");
  const [dispatchNote, setDispatchNote] = useState<string>("");
  const [dispatchQrInput, setDispatchQrInput] = useState<string>("");
  const [dispatchQrCodes, setDispatchQrCodes] = useState<string[]>([]);
  const [dispatching, setDispatching] = useState<boolean>(false);
  const dispatchQrInputRef = useRef<HTMLInputElement>(null);

  // Receive Modal state
  const [receiveModalOpen, setReceiveModalOpen] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState<any>(null);
  const [receiveQtyInput, setReceiveQtyInput] = useState<string>("");
  const [receiveNote, setReceiveNote] = useState<string>("");
  const [receiveQrInput, setReceiveQrInput] = useState<string>("");
  const [receiveScannedQrs, setReceiveScannedQrs] = useState<string[]>([]);
  const [receiving, setReceiving] = useState<boolean>(false);
  const receiveQrInputRef = useRef<HTMLInputElement>(null);

  // Fetch branches
  const { data: subbranchesData } = useQuery(["SubBranchesForTransfers"], () =>
    ListCommanWay("/admin/subbranch/list", {}, { paginate: 1000 })
  );

  const subbranchOptions = (subbranchesData?.data?.data || []).map((sb: any) => ({
    value: sb.id,
    label: `${sb.branch_name || sb.name || "Hub"} ${sb.code ? `(${sb.code})` : ""}`,
  }));

  // Auto-fill user's branch
  useEffect(() => {
    if (subbranchOptions.length > 0) {
      if (userSubbranchId) {
        const found = subbranchOptions.find((o: any) => o.value === userSubbranchId);
        if (found && !selectedBranchFilter) setSelectedBranchFilter(found);
        if (found && !dispatchFromBranch) setDispatchFromBranch(found);
      } else {
        if (!selectedBranchFilter) setSelectedBranchFilter(subbranchOptions[0]);
        if (!dispatchFromBranch) setDispatchFromBranch(subbranchOptions[0]);
      }
    }
  }, [subbranchOptions, userSubbranchId]);

  // Fetch transfers list
  const fetchTransfers = async () => {
    setLoading(true);
    try {
      const payload: any = {
        options: { page: 1, limit: 50 },
      };

      if (activeSubtab === "incoming") {
        if (selectedBranchFilter?.value) payload.toSubbranch = selectedBranchFilter.value;
      } else {
        if (selectedBranchFilter?.value) payload.fromSubbranch = selectedBranchFilter.value;
      }

      if (statusFilter !== "ALL") {
        payload.status = statusFilter;
      }

      const res: any = await request({
        url: "/admin/inventory-transfer/list",
        method: "POST",
        body: payload,
      });

      if (res?.status === "SUCCESS" && res.data) {
        setTransfers(res.data.data || []);
        setTotalCount(res.data.paginator?.itemCount ?? (res.data.data || []).length);
      } else {
        setTransfers([]);
        setTotalCount(0);
      }
    } catch {
      setTransfers([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransfers();
  }, [activeSubtab, selectedBranchFilter, statusFilter]);

  // Count pending incoming transfers
  const pendingIncomingCount = activeSubtab === "incoming"
    ? transfers.filter((t) => t.status === "IN_TRANSIT" || t.status === "PARTIALLY_RECEIVED").length
    : 0;

  // ── Fast Handheld Scan Handler ──
  const handleFastScanSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const raw = fastScanInput.trim().toUpperCase();
    if (!raw) return;

    if (isAwbFormat(raw)) {
      soundFX.playError();
      const err = `This looks like an AWB number (${raw})! Transfer in-scan requires a Box QR (e.g. 123123, BX-123123, AHD123123).`;
      setFastScanResult({ success: false, message: err });
      UseToast(err, "error");
      setFastScanInput("");
      return;
    }

    setFastScanLoading(true);
    setFastScanResult(null);

    try {
      const res: any = await request({
        url: "/admin/inventory-transfer/scan-box",
        method: "POST",
        body: { qrCode: raw },
      });

      if (res?.status === "SUCCESS" && res.data?.success) {
        soundFX.playSuccess();
        const msg = res.data.message || `Box '${raw}' received successfully!`;
        setFastScanResult({ success: true, message: msg, transfer: res.data.transfer });
        UseToast(msg, "success");
        setFastScanInput("");
        fetchTransfers();
      } else {
        soundFX.playError();
        const err = res?.message || res?.data?.message || `Failed to receive box '${raw}'.`;
        setFastScanResult({ success: false, message: err });
        UseToast(err, "error");
      }
    } catch (err: any) {
      soundFX.playError();
      const errText = typeof err === "string" ? err : err?.message || `Error scanning box '${raw}'.`;
      setFastScanResult({ success: false, message: errText });
      UseToast(errText, "error");
    } finally {
      setFastScanLoading(false);
      fastScanInputRef.current?.focus();
    }
  };

  // ── Dispatch: Add QR code ──
  const handleAddDispatchQr = (e: React.FormEvent) => {
    e.preventDefault();
    const raw = dispatchQrInput.trim().toUpperCase();
    if (!raw) return;

    if (isAwbFormat(raw)) {
      soundFX.playError();
      UseToast(`'${raw}' is an AWB number! Stock transfer requires Box QRs (e.g. 123123, BX-123123, AHD123123).`, "error");
      setDispatchQrInput("");
      return;
    }

    if (!isBoxQrFormat(raw)) {
      soundFX.playError();
      UseToast(`Invalid Box QR format '${raw}'. Valid: numeric (123123) or prefixed (BX-123123, AHD123123).`, "warning");
      return;
    }

    if (dispatchQrCodes.includes(raw)) {
      soundFX.playError();
      UseToast(`Box '${raw}' is already added to this transfer dispatch!`, "warning");
      setDispatchQrInput("");
      return;
    }

    soundFX.playSuccess();
    setDispatchQrCodes((prev) => [...prev, raw]);
    setDispatchQrInput("");
    dispatchQrInputRef.current?.focus();
  };

  const handleRemoveDispatchQr = (code: string) => {
    setDispatchQrCodes((prev) => prev.filter((c) => c !== code));
  };

  // ── Dispatch: Submit ──
  const handleDispatchSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!dispatchFromBranch?.value) {
      UseToast("Please select origin (From) branch", "error");
      return;
    }
    if (!dispatchToBranch?.value) {
      UseToast("Please select destination (To) branch", "error");
      return;
    }
    if (dispatchFromBranch.value === dispatchToBranch.value) {
      UseToast("Origin and destination branches cannot be the same!", "error");
      return;
    }
    if (!dispatchName?.value) {
      UseToast("Please select an item name", "error");
      return;
    }

    const payload: any = {
      fromSubbranch: dispatchFromBranch.value,
      toSubbranch: dispatchToBranch.value,
      name: dispatchName.value,
      category: dispatchCategory?.value?.toLowerCase() || "packaging",
      dispatchNote: dispatchNote.trim() || undefined,
    };

    if (dispatchMode === "qr") {
      if (dispatchQrCodes.length === 0) {
        UseToast("Please scan or add at least one Box QR code for this transfer!", "error");
        return;
      }
      payload.qrCodes = dispatchQrCodes;
    } else {
      const q = Number(dispatchQty);
      if (isNaN(q) || q <= 0) {
        UseToast("Please enter a valid transfer quantity", "error");
        return;
      }
      payload.qty = q;
    }

    const result = await Swal.fire({
      title: "Confirm Stock Dispatch",
      text: `Dispatch ${dispatchMode === "qr" ? dispatchQrCodes.length : dispatchQty} units of '${dispatchName.value}' from ${dispatchFromBranch.label} to ${dispatchToBranch.label}? Stock will be placed in transit.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#0077B6",
      confirmButtonText: "Yes, Dispatch Now",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    setDispatching(true);
    try {
      const res: any = await request({
        url: "/admin/inventory-transfer/dispatch",
        method: "POST",
        body: payload,
      });

      if (res?.status === "SUCCESS" && res.data?.success) {
        soundFX.playSuccess();
        const t = res.data.transfer;
        UseToast(
          res.data.message || `Transfer ${t?.transferNo || ""} dispatched successfully! Stock is in-transit to ${dispatchToBranch.label}.`,
          "success"
        );
        setDispatchModalOpen(false);
        setDispatchQrCodes([]);
        setDispatchNote("");
        fetchTransfers();
      } else {
        soundFX.playError();
        UseToast(res?.message || res?.data?.message || "Failed to dispatch transfer", "error");
      }
    } catch (err: any) {
      soundFX.playError();
      UseToast(typeof err === "string" ? err : err?.message || "Error dispatching transfer", "error");
    } finally {
      setDispatching(false);
    }
  };

  // ── Open Receive Modal ──
  const handleOpenReceive = (transfer: any) => {
    setSelectedTransfer(transfer);
    const pendingQty = Math.max(0, (transfer.qty || 0) - (transfer.receivedQty || 0));
    setReceiveQtyInput(String(pendingQty));
    setReceiveNote("");
    setReceiveScannedQrs([]);
    setReceiveModalOpen(true);
  };

  // ── Receive: Add QR code ──
  const handleAddReceiveQr = (e: React.FormEvent) => {
    e.preventDefault();
    const raw = receiveQrInput.trim().toUpperCase();
    if (!raw) return;

    if (receiveScannedQrs.includes(raw)) {
      soundFX.playError();
      UseToast(`Box '${raw}' is already scanned in this receive batch!`, "warning");
      setReceiveQrInput("");
      return;
    }

    soundFX.playSuccess();
    setReceiveScannedQrs((prev) => [...prev, raw]);
    setReceiveQrInput("");
    receiveQrInputRef.current?.focus();
  };

  // ── Receive: Submit ──
  const handleReceiveSubmit = async () => {
    if (!selectedTransfer) return;

    const payload: any = {
      transferId: selectedTransfer.id,
      receiveNote: receiveNote.trim() || undefined,
    };

    if (selectedTransfer.hasQr) {
      if (receiveScannedQrs.length === 0) {
        UseToast("Please scan at least 1 box QR code to receive, or click 'Receive All Pending'", "error");
        return;
      }
      payload.qrCodes = receiveScannedQrs;
    } else {
      const q = Number(receiveQtyInput);
      if (isNaN(q) || q <= 0) {
        UseToast("Please enter a valid quantity to receive", "error");
        return;
      }
      payload.qty = q;
    }

    setReceiving(true);
    try {
      const res: any = await request({
        url: "/admin/inventory-transfer/receive",
        method: "POST",
        body: payload,
      });

      if (res?.status === "SUCCESS" && res.data?.success) {
        soundFX.playSuccess();
        UseToast(res.data.message || "Stock received successfully into stock!", "success");
        setReceiveModalOpen(false);
        fetchTransfers();
      } else {
        soundFX.playError();
        UseToast(res?.message || res?.data?.message || "Failed to receive transfer", "error");
      }
    } catch (err: any) {
      soundFX.playError();
      UseToast(typeof err === "string" ? err : err?.message || "Error receiving transfer", "error");
    } finally {
      setReceiving(false);
    }
  };

  // ── Shortcut: Receive All Pending for QR Transfer ──
  const handleReceiveAllPending = async () => {
    if (!selectedTransfer) return;
    const allPending = (selectedTransfer.qrCodes || []).filter(
      (code: string) => !(selectedTransfer.receivedQrCodes || []).includes(code)
    );

    if (allPending.length === 0) {
      UseToast("All boxes are already received!", "warning");
      return;
    }

    setReceiving(true);
    try {
      const res: any = await request({
        url: "/admin/inventory-transfer/receive",
        method: "POST",
        body: {
          transferId: selectedTransfer.id,
          qrCodes: allPending,
          receiveNote: receiveNote.trim() || "All remaining boxes received in full",
        },
      });

      if (res?.status === "SUCCESS" && res.data?.success) {
        soundFX.playSuccess();
        UseToast(res.data.message || `All ${allPending.length} boxes successfully received into stock!`, "success");
        setReceiveModalOpen(false);
        fetchTransfers();
      } else {
        soundFX.playError();
        UseToast(res?.message || "Failed to receive transfer", "error");
      }
    } catch (err: any) {
      soundFX.playError();
      UseToast(typeof err === "string" ? err : err?.message || "Error receiving transfer", "error");
    } finally {
      setReceiving(false);
    }
  };

  // ── Cancel Transfer ──
  const handleCancelTransfer = async (transfer: any) => {
    const result = await Swal.fire({
      title: `Cancel Transfer ${transfer.transferNo}?`,
      text: `This will cancel the transfer and automatically RESTORE ${transfer.qty} units of '${transfer.name}' back to ${transfer._fromSubbranch?.branch_name || "origin branch"}.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#e11d48",
      confirmButtonText: "Yes, Cancel Transfer",
      cancelButtonText: "Back",
    });

    if (!result.isConfirmed) return;

    try {
      const res: any = await request({
        url: `/admin/inventory-transfer/cancel/${transfer.id}`,
        method: "POST",
      });

      if (res?.status === "SUCCESS") {
        soundFX.playSuccess();
        UseToast(res.message || `Transfer ${transfer.transferNo} cancelled and stock restored.`, "success");
        fetchTransfers();
      } else {
        soundFX.playError();
        UseToast(res?.message || "Failed to cancel transfer", "error");
      }
    } catch (err: any) {
      soundFX.playError();
      UseToast(typeof err === "string" ? err : err?.message || "Error cancelling transfer", "error");
    }
  };

  return (
    <div className="space-y-4">
      {/* Top Action & Subtab Bar */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-xs flex flex-row md:flex-col items-center md:items-start justify-between gap-4">
        <div className="flex items-center gap-2">
          {/* Subtab Buttons */}
          <div className="flex p-1 bg-slate-100 rounded-lg text-xs font-semibold">
            <button
              type="button"
              onClick={() => setActiveSubtab("incoming")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md transition-all ${
                activeSubtab === "incoming"
                  ? "bg-white text-[#0077B6] shadow-xs font-bold"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              <ArrowDownTrayIcon className="w-4 h-4 text-emerald-600" />
              <span>Incoming Transfers</span>
              {pendingIncomingCount > 0 && (
                <span className="bg-emerald-100 text-emerald-800 text-[10px] font-bold px-1.5 py-0.2 rounded-full">
                  {pendingIncomingCount}
                </span>
              )}
            </button>

            <button
              type="button"
              onClick={() => setActiveSubtab("outgoing")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md transition-all ${
                activeSubtab === "outgoing"
                  ? "bg-white text-[#0077B6] shadow-xs font-bold"
                  : "text-slate-600 hover:text-slate-900"
              }`}
            >
              <ArrowUpTrayIcon className="w-4 h-4 text-sky-600" />
              <span>Outgoing Dispatches</span>
            </button>
          </div>
        </div>

        {/* Filters & Dispatch Button */}
        <div className="flex items-center gap-2 flex-wrap w-auto md:w-full justify-end md:justify-start">
          <div className="w-48 text-xs">
            <Select
              options={subbranchOptions}
              value={selectedBranchFilter}
              onChange={setSelectedBranchFilter}
              styles={compactSelectStyles}
              placeholder="Filter branch..."
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="text-xs px-2.5 py-2 border border-slate-300 rounded-lg bg-white font-medium text-slate-700 outline-none focus:border-[#0077B6]"
          >
            <option value="ALL">All Statuses</option>
            <option value="IN_TRANSIT">In Transit</option>
            <option value="PARTIALLY_RECEIVED">Partially Received</option>
            <option value="COMPLETED">Completed</option>
            <option value="CANCELLED">Cancelled</option>
          </select>

          <button
            type="button"
            onClick={() => {
              setDispatchQrCodes([]);
              setDispatchNote("");
              setDispatchModalOpen(true);
            }}
            className="px-3.5 py-2 bg-[#0077B6] hover:bg-[#023e8a] text-white text-xs font-bold rounded-lg shadow-xs flex items-center gap-1.5 transition-all"
          >
            <PaperAirplaneIcon className="w-4 h-4" />
            <span>Dispatch New Transfer</span>
          </button>
        </div>
      </div>

      {/* Fast In-Scan Card (Shown on Incoming Tab) */}
      {activeSubtab === "incoming" && (
        <div className="bg-gradient-to-r from-emerald-500/10 via-teal-500/5 to-transparent bg-white rounded-xl border border-emerald-200 p-4 shadow-xs space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <QrCodeIcon className="w-5 h-5 text-emerald-600" />
              <h3 className="text-xs font-bold uppercase tracking-wider text-emerald-950">
                Fast Handheld In-Scan (Incoming Boxes)
              </h3>
            </div>
            <span className="text-[11px] text-emerald-700 font-medium">
              Scan any received box QR &bull; Auto-matches active incoming transfer
            </span>
          </div>

          <form onSubmit={handleFastScanSubmit} className="relative">
            <input
              ref={fastScanInputRef}
              type="text"
              value={fastScanInput}
              onChange={(e) => setFastScanInput(e.target.value.toUpperCase())}
              placeholder="Scan incoming box QR code (e.g. 123123, BX-123123, AHD123123)..."
              className="w-full pl-10 pr-24 py-2.5 text-sm font-mono font-bold border-2 border-emerald-300 rounded-xl focus:outline-none focus:border-emerald-600 bg-white"
            />
            <QrCodeIcon className="w-5 h-5 text-emerald-600 absolute left-3 top-3" />
            <button
              type="submit"
              disabled={fastScanLoading || !fastScanInput.trim()}
              className="absolute right-1.5 top-1.5 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg transition-colors disabled:opacity-40"
            >
              {fastScanLoading ? "Scanning..." : "In-Scan"}
            </button>
          </form>

          {fastScanResult && (
            <div
              className={`p-2.5 rounded-lg border text-xs flex items-center justify-between animate-fadeIn ${
                fastScanResult.success
                  ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                  : "bg-rose-50 border-rose-200 text-rose-900"
              }`}
            >
              <div className="flex items-center gap-2">
                {fastScanResult.success ? (
                  <CheckCircleIcon className="w-4 h-4 text-emerald-600 shrink-0" />
                ) : (
                  <ExclamationTriangleIcon className="w-4 h-4 text-rose-600 shrink-0" />
                )}
                <span>{fastScanResult.message}</span>
              </div>
              <button
                type="button"
                onClick={() => setFastScanResult(null)}
                className="text-slate-400 hover:text-slate-600"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Transfers List Table Card */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-xs space-y-3">
        <div className="flex items-center justify-between pb-2 border-b border-slate-100">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2">
            <ArrowsRightLeftIcon className="w-4 h-4 text-[#0077B6]" />
            <span>
              {activeSubtab === "incoming" ? "Incoming Transfers" : "Outgoing Dispatches"} ({transfers.length})
            </span>
          </h3>
          <span className="text-xs text-slate-400">Total: {totalCount} records</span>
        </div>

        {loading ? (
          <div className="py-16 text-center text-xs text-slate-400">Loading transfers...</div>
        ) : transfers.length === 0 ? (
          <div className="py-16 text-center text-xs text-slate-400 border border-dashed rounded-xl">
            No {activeSubtab} transfers found for this branch filter.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 border-b text-[11px] font-bold uppercase text-slate-500">
                <tr>
                  <th className="py-2.5 px-3">Transfer #</th>
                  <th className="py-2.5 px-3">
                    {activeSubtab === "incoming" ? "From Branch" : "To Branch"}
                  </th>
                  <th className="py-2.5 px-3">Item / Category</th>
                  <th className="py-2.5 px-3">Type</th>
                  <th className="py-2.5 px-3">Progress</th>
                  <th className="py-2.5 px-3">Status</th>
                  <th className="py-2.5 px-3">Dispatched</th>
                  <th className="py-2.5 px-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {transfers.map((t) => {
                  const received = t.receivedQty || 0;
                  const total = t.qty || 0;
                  const percent = total > 0 ? Math.round((received / total) * 100) : 0;
                  const isIncoming = activeSubtab === "incoming";
                  const canReceive = isIncoming && (t.status === "IN_TRANSIT" || t.status === "PARTIALLY_RECEIVED");
                  const canCancel = !isIncoming && t.status === "IN_TRANSIT" && received === 0;

                  return (
                    <tr key={t.id} className="hover:bg-slate-50">
                      <td className="py-2.5 px-3 font-mono font-bold text-slate-800">
                        {t.transferNo}
                      </td>
                      <td className="py-2.5 px-3 text-slate-700">
                        <div className="flex items-center gap-1">
                          <BuildingOfficeIcon className="w-3.5 h-3.5 text-slate-400" />
                          <span>
                            {isIncoming
                              ? t._fromSubbranch?.branch_name || `Hub #${t.fromSubbranch}`
                              : t._toSubbranch?.branch_name || `Hub #${t.toSubbranch}`}
                          </span>
                        </div>
                      </td>
                      <td className="py-2.5 px-3">
                        <span className="font-semibold text-slate-800">{t.name}</span>
                        <span className="block text-[10px] text-slate-400 uppercase">
                          {t.category || "packaging"}
                        </span>
                      </td>
                      <td className="py-2.5 px-3">
                        {t.hasQr ? (
                          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#0077B6] bg-sky-50 px-2 py-0.5 rounded border border-sky-100">
                            <QrCodeIcon className="w-3.5 h-3.5" />
                            <span>QR Tracked</span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-600 bg-slate-100 px-2 py-0.5 rounded">
                            <CubeIcon className="w-3.5 h-3.5" />
                            <span>Bulk</span>
                          </span>
                        )}
                      </td>
                      <td className="py-2.5 px-3">
                        <div className="space-y-1">
                          <div className="flex items-center justify-between text-[11px] font-mono">
                            <span className="font-bold text-slate-700">{received} / {total}</span>
                            <span className="text-[10px] text-slate-400">{percent}%</span>
                          </div>
                          <div className="w-24 bg-slate-200 rounded-full h-1.5 overflow-hidden">
                            <div
                              className={`h-1.5 rounded-full ${
                                percent === 100 ? "bg-emerald-500" : percent > 0 ? "bg-amber-500" : "bg-slate-300"
                              }`}
                              style={{ width: `${percent}%` }}
                            />
                          </div>
                        </div>
                      </td>
                      <td className="py-2.5 px-3">
                        <span
                          className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${
                            t.status === "COMPLETED"
                              ? "bg-emerald-100 text-emerald-800 border border-emerald-200"
                              : t.status === "PARTIALLY_RECEIVED"
                              ? "bg-amber-100 text-amber-800 border border-amber-200"
                              : t.status === "CANCELLED"
                              ? "bg-slate-100 text-slate-600"
                              : "bg-sky-100 text-sky-800 border border-sky-200"
                          }`}
                        >
                          {t.status === "IN_TRANSIT" ? "In Transit" : t.status === "PARTIALLY_RECEIVED" ? "Partial" : t.status}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 text-slate-500 text-[11px]">
                        <div>{t.dispatchedAt || "—"}</div>
                        {t.dispatchNote && (
                          <div className="text-[10px] text-slate-400 truncate max-w-[120px]" title={t.dispatchNote}>
                            {t.dispatchNote}
                          </div>
                        )}
                      </td>
                      <td className="py-2.5 px-3 text-right">
                        {canReceive && (
                          <button
                            type="button"
                            onClick={() => handleOpenReceive(t)}
                            className="px-2.5 py-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-[11px] rounded shadow-xs"
                          >
                            Receive / In-Scan
                          </button>
                        )}
                        {canCancel && (
                          <button
                            type="button"
                            onClick={() => handleCancelTransfer(t)}
                            className="text-rose-600 hover:text-rose-800 text-[11px] font-semibold underline"
                          >
                            Cancel
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Modal: Dispatch Stock Transfer ── */}
      {dispatchModalOpen && (
        <div className="fixed inset-0 z-[9999] overflow-y-auto bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
            <div className="px-5 py-4 bg-slate-900 text-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                <PaperAirplaneIcon className="w-5 h-5 text-sky-400" />
                <h3 className="text-sm font-bold">Dispatch Stock Transfer</h3>
              </div>
              <button
                type="button"
                onClick={() => setDispatchModalOpen(false)}
                className="text-white/70 hover:text-white"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleDispatchSubmit} className="p-5 space-y-4 overflow-y-auto">
              {/* Transfer Mode Switch */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Transfer Mode</label>
                <div className="flex p-1 bg-slate-100 rounded-lg text-xs font-semibold">
                  <button
                    type="button"
                    onClick={() => setDispatchMode("bulk")}
                    className={`flex-1 py-1.5 rounded-md transition-all ${
                      dispatchMode === "bulk"
                        ? "bg-white text-slate-800 shadow-xs font-bold"
                        : "text-slate-500 hover:text-slate-800"
                    }`}
                  >
                    Bulk Transfer (No QR)
                  </button>
                  <button
                    type="button"
                    onClick={() => setDispatchMode("qr")}
                    className={`flex-1 py-1.5 rounded-md transition-all ${
                      dispatchMode === "qr"
                        ? "bg-white text-[#0077B6] shadow-xs font-bold"
                        : "text-slate-500 hover:text-slate-800"
                    }`}
                  >
                    Scan Box QRs ({dispatchQrCodes.length})
                  </button>
                </div>
              </div>

              {/* Origin and Destination Branch */}
              <div className="grid grid-cols-2 sm:grid-cols-1 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">From Hub *</label>
                  <Select
                    options={subbranchOptions}
                    value={dispatchFromBranch}
                    onChange={setDispatchFromBranch}
                    styles={compactSelectStyles}
                    placeholder="Source hub..."
                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">To Hub *</label>
                  <Select
                    options={subbranchOptions}
                    value={dispatchToBranch}
                    onChange={setDispatchToBranch}
                    styles={compactSelectStyles}
                    placeholder="Destination hub..."
                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  />
                </div>
              </div>

              {/* Item Name & Category */}
              <div className="grid grid-cols-2 sm:grid-cols-1 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Item Name *</label>
                  <Select
                    options={STOCK_ITEM_OPTIONS.filter((o) => o.value !== "OTHER")}
                    value={dispatchName}
                    onChange={setDispatchName}
                    styles={compactSelectStyles}
                    placeholder="Select item..."
                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Category</label>
                  <Select
                    options={CATEGORY_OPTIONS}
                    value={dispatchCategory}
                    onChange={setDispatchCategory}
                    styles={compactSelectStyles}
                    placeholder="Category..."
                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                  />
                </div>
              </div>

              {/* Mode-Specific Inputs */}
              {dispatchMode === "bulk" ? (
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Quantity *</label>
                  <input
                    type="number"
                    min="1"
                    value={dispatchQty}
                    onChange={(e) => setDispatchQty(e.target.value)}
                    placeholder="Enter quantity to transfer (e.g. 5)..."
                    className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg font-mono font-bold"
                    required
                  />
                </div>
              ) : (
                <div className="space-y-2 p-3 bg-slate-50 border border-slate-200 rounded-xl">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                      <QrCodeIcon className="w-4 h-4 text-[#0077B6]" />
                      <span>Scan Outgoing Box QRs ({dispatchQrCodes.length})</span>
                    </label>
                    {dispatchQrCodes.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setDispatchQrCodes([])}
                        className="text-[11px] text-rose-600 hover:underline"
                      >
                        Clear All
                      </button>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <input
                      ref={dispatchQrInputRef}
                      type="text"
                      value={dispatchQrInput}
                      onChange={(e) => setDispatchQrInput(e.target.value.toUpperCase())}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddDispatchQr(e);
                        }
                      }}
                      placeholder="Scan/type Box QR (e.g. 123123, BX-123123, AHD123123)..."
                      className="flex-1 px-3 py-1.5 text-xs font-mono font-bold border border-slate-300 rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={handleAddDispatchQr}
                      className="px-3 py-1.5 bg-[#0077B6] hover:bg-[#023e8a] text-white text-xs font-bold rounded-lg"
                    >
                      Add
                    </button>
                  </div>

                  {dispatchQrCodes.length > 0 && (
                    <div className="max-h-32 overflow-y-auto flex flex-wrap gap-1.5 pt-1">
                      {dispatchQrCodes.map((code) => (
                        <span
                          key={code}
                          className="inline-flex items-center gap-1 bg-sky-50 text-[#0077B6] font-mono text-[11px] font-bold px-2 py-0.5 rounded border border-sky-200"
                        >
                          <span>{code}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveDispatchQr(code)}
                            className="text-sky-400 hover:text-rose-600 ml-0.5"
                          >
                            &times;
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Dispatch Note */}
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Dispatch Note / Vehicle Info (optional)
                </label>
                <input
                  type="text"
                  value={dispatchNote}
                  onChange={(e) => setDispatchNote(e.target.value)}
                  placeholder="e.g. Dispatched via surface cargo truck #GJ01-1234..."
                  className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg"
                />
              </div>

              {/* Informative Lifecycle Box */}
              <div className="p-3 bg-sky-50 border border-sky-200 rounded-xl text-xs text-sky-900 space-y-1">
                <div className="font-bold flex items-center gap-1.5">
                  <ClockIcon className="w-4 h-4 text-[#0077B6]" />
                  <span>In-Transit Dispatch Flow</span>
                </div>
                <p className="text-[11px] text-sky-800 leading-relaxed">
                  Stock will be immediately deducted from{" "}
                  <strong>{dispatchFromBranch?.label || "Origin"}</strong> and placed in-transit (`TR-XXXX`).
                  Destination will only receive stock once staff in-scans and receives the transfer.
                </p>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-2 border-t">
                <button
                  type="button"
                  onClick={() => setDispatchModalOpen(false)}
                  className="px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-100 rounded-lg font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={dispatching}
                  className="px-4 py-1.5 bg-[#0077B6] hover:bg-[#023e8a] text-white text-xs font-bold rounded-lg disabled:opacity-40"
                >
                  {dispatching ? "Dispatching..." : "Confirm & Dispatch"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modal: Receive / In-Scan Transfer ── */}
      {receiveModalOpen && selectedTransfer && (
        <div className="fixed inset-0 z-[9999] overflow-y-auto bg-black/50 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
            <div className="px-5 py-4 bg-emerald-700 text-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ArrowDownTrayIcon className="w-5 h-5 text-white" />
                <h3 className="text-sm font-bold">
                  In-Scan & Receive Transfer #{selectedTransfer.transferNo}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setReceiveModalOpen(false)}
                className="text-white/70 hover:text-white"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-4 overflow-y-auto">
              <div className="p-3 bg-slate-50 border rounded-xl text-xs space-y-1">
                <div className="flex items-center justify-between font-bold text-slate-800">
                  <span>Item: {selectedTransfer.name}</span>
                  <span className="font-mono text-[#0077B6]">{selectedTransfer.hasQr ? "QR Tracked" : "Bulk"}</span>
                </div>
                <div className="text-slate-600 text-[11px]">
                  Origin: {selectedTransfer._fromSubbranch?.branch_name || `Hub #${selectedTransfer.fromSubbranch}`}
                </div>
                <div className="text-slate-600 text-[11px]">
                  Received: <strong className="text-emerald-700">{selectedTransfer.receivedQty || 0}</strong> of <strong>{selectedTransfer.qty}</strong> units
                </div>
              </div>

              {selectedTransfer.hasQr ? (
                /* QR Tracked Receiving */
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-bold text-slate-700 flex items-center gap-1.5">
                      <QrCodeIcon className="w-4 h-4 text-emerald-600" />
                      <span>Scan Incoming Box QRs ({receiveScannedQrs.length} scanned)</span>
                    </label>
                    <button
                      type="button"
                      onClick={handleReceiveAllPending}
                      className="text-xs text-emerald-700 font-bold hover:underline"
                    >
                      Receive All Pending
                    </button>
                  </div>

                  <form onSubmit={handleAddReceiveQr} className="flex gap-2">
                    <input
                      ref={receiveQrInputRef}
                      type="text"
                      value={receiveQrInput}
                      onChange={(e) => setReceiveQrInput(e.target.value.toUpperCase())}
                      placeholder="Scan Box QR (e.g. 123123, BX-123123)..."
                      className="flex-1 px-3 py-2 text-xs font-mono font-bold border border-slate-300 rounded-lg focus:border-emerald-600 outline-none"
                      autoFocus
                    />
                    <button
                      type="submit"
                      className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg"
                    >
                      Add
                    </button>
                  </form>

                  {/* Expected QR status list */}
                  <div className="space-y-1 max-h-40 overflow-y-auto border rounded-xl p-2.5 bg-slate-50">
                    <span className="text-[10px] uppercase font-bold text-slate-400 block mb-1">
                      Expected Transfer Boxes
                    </span>
                    <div className="flex flex-wrap gap-1.5">
                      {(selectedTransfer.qrCodes || []).map((code: string) => {
                        const isAlreadyReceived = (selectedTransfer.receivedQrCodes || []).includes(code);
                        const isStagedNow = receiveScannedQrs.includes(code);

                        return (
                          <span
                            key={code}
                            className={`inline-flex items-center gap-1 font-mono text-[11px] font-bold px-2 py-0.5 rounded border ${
                              isAlreadyReceived
                                ? "bg-emerald-100 text-emerald-800 border-emerald-300"
                                : isStagedNow
                                ? "bg-amber-100 text-amber-900 border-amber-300"
                                : "bg-white text-slate-600 border-slate-200"
                            }`}
                          >
                            <span>{code}</span>
                            {isAlreadyReceived ? (
                              <CheckCircleIcon className="w-3.5 h-3.5 text-emerald-600" />
                            ) : isStagedNow ? (
                              <button
                                type="button"
                                onClick={() => setReceiveScannedQrs((prev) => prev.filter((c) => c !== code))}
                                className="text-amber-700 hover:text-rose-600 font-bold ml-1"
                              >
                                &times;
                              </button>
                            ) : null}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : (
                /* Bulk Receiving */
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Quantity Received Now *
                  </label>
                  <input
                    type="number"
                    min="1"
                    max={Math.max(1, selectedTransfer.qty - (selectedTransfer.receivedQty || 0))}
                    value={receiveQtyInput}
                    onChange={(e) => setReceiveQtyInput(e.target.value)}
                    className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg font-mono font-bold"
                    required
                  />
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  Receiving Condition Note (optional)
                </label>
                <input
                  type="text"
                  value={receiveNote}
                  onChange={(e) => setReceiveNote(e.target.value)}
                  placeholder="e.g. Received in good condition, unloaded at warehouse..."
                  className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t">
                <button
                  type="button"
                  onClick={() => setReceiveModalOpen(false)}
                  className="px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-100 rounded-lg font-medium"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={receiving}
                  onClick={handleReceiveSubmit}
                  className="px-4 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-lg disabled:opacity-40"
                >
                  {receiving ? "Processing..." : "Confirm Receipt"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
